/**
 * Client-side HMAC Computation
 * Uses Web Crypto API (SubtleCrypto)
 */

/**
 * Compute HMAC-SHA256 signature
 *
 * @param projectId - Project ID from config
 * @param payload - JSON payload (stringified)
 * @param apiKey - API key (used as HMAC secret)
 * @returns Hex-encoded HMAC signature
 */
export async function computeHMAC(
  projectId: string,
  payload: string,
  apiKey: string
): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto API not available');
  }

  const encoder = new TextEncoder();

  // Import API key as HMAC key
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(apiKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  // Message to sign: projectId + payload
  const message = projectId + payload;
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message));

  // Convert to hex string
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
